import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Input, Label } from '@fluxora/ui';

import { useResumeConfirmMutation, useResumeConfirmSkipMutation } from '../api/resume.queries';
import { replaceLocation } from '../services/browser-navigation';
import {
  RESUME_RESTART_STATE,
  decideResumeConfirm,
  mismatchKey,
  normalizeConfirmPhone,
} from '../services/resume-confirm.service';

/**
 * `/resume/confirm` (E-CRM04) — a resume link sent to an UNVERIFIED email lands
 * here. The BFF holds the lead's details until the person types the phone number
 * they gave us; nothing is shown or prefilled before that.
 *
 *   match    → a FULL navigation to where the lead left off (so the hydration
 *              gate reads the seeded session and the welcome-back banner shows);
 *   mismatch → "That number doesn't match — N tries left";
 *   lockout / expiry / "Start over instead" → `/` with a neutral notice.
 *
 * A `<div>`, not a `<main>`: the storefront layout owns the one main landmark.
 */
export function ResumeConfirmPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useResumeConfirmMutation();
  const skip = useResumeConfirmSkipMutation();

  const [phone, setPhone] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);

  const restart = (): void => {
    navigate('/', { replace: true, state: RESUME_RESTART_STATE });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const digits = normalizeConfirmPhone(phone);
    setInvalid(digits === null);
    if (digits === null) return;
    confirm.mutate(
      { phone: digits },
      {
        onSuccess: (res) => {
          const action = decideResumeConfirm(res);
          if (action.kind === 'restored') replaceLocation(action.to);
          else if (action.kind === 'retry') setAttemptsLeft(action.attemptsLeft);
          else restart();
        },
      },
    );
  };

  const handleSkip = (): void => {
    skip.mutate(undefined, { onSettled: restart });
  };

  const errorId = 'resume-confirm-phone-error';
  const errorText = invalid
    ? t('shop.resume.confirm.invalid')
    : attemptsLeft !== null
      ? t(mismatchKey(attemptsLeft), { count: attemptsLeft })
      : null;

  return (
    <div className="mx-auto flex max-w-md flex-col gap-5 px-4 py-10">
      <h1 className="text-xl font-bold text-primary">{t('shop.resume.confirm.heading')}</h1>
      <p className="text-sm text-muted">{t('shop.resume.confirm.body')}</p>

      <form className="flex flex-col gap-3" onSubmit={handleSubmit} noValidate>
        <Label htmlFor="resume-confirm-phone">{t('shop.resume.confirm.phoneLabel')}</Label>
        <Input
          id="resume-confirm-phone"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          maxLength={20}
          value={phone}
          hasError={errorText !== null}
          aria-invalid={errorText !== null}
          aria-describedby={errorText !== null ? errorId : undefined}
          onChange={(e) => {
            setPhone(e.target.value);
            setInvalid(false);
          }}
        />
        {errorText !== null ? (
          <p id={errorId} role="alert" className="text-sm text-danger">
            {errorText}
          </p>
        ) : null}
        {confirm.isError ? (
          <Alert variant="danger" title={t('shop.resume.confirm.failedTitle')}>
            {t('shop.resume.confirm.failedBody')}
          </Alert>
        ) : null}
        <Button type="submit" variant="primary" shape="pill" isLoading={confirm.isPending}>
          {t('shop.resume.confirm.continue')}
        </Button>
      </form>

      <Link
        to="/"
        className="text-sm font-medium text-primary underline"
        onClick={(e) => {
          e.preventDefault();
          handleSkip();
        }}
      >
        {t('shop.resume.confirm.startOver')}
      </Link>
    </div>
  );
}
